// Monitor de Erros para detectar problemas específicos em produção
// Especialmente útil para monitorar erros React #130 e problemas do Service Worker

class ErrorMonitor {
  constructor() {
    this.errors = [];
    this.maxErrors = 50;
    this.initialized = false;

    this.init();
  }

  init() {
    if (this.initialized) return;

    console.log('🔍 Inicializando ErrorMonitor...');

    // Interceptar erros de JavaScript
    window.addEventListener('error', this.handleError.bind(this));

    // Interceptar promises rejeitadas
    window.addEventListener('unhandledrejection', this.handlePromiseRejection.bind(this));

    // Interceptar erros do React (já tratados pelo Error Boundary, mas backup)
    const originalConsoleError = console.error;
    console.error = (...args) => {
      this.handleConsoleError(...args);
      originalConsoleError.apply(console, args);
    };

    this.initialized = true;
    console.log('✅ ErrorMonitor inicializado');
  }

  handleError(event) {
    const error = {
      type: 'javascript_error',
      timestamp: new Date().toISOString(),
      message: event.message,
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
      error: event.error,
      stack: event.error?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.logError('JavaScript Error', error);
    this.storeError(error);

    // Verificar se é erro React #130
    if (this.isReactError130(error)) {
      this.handleReactError130(error);
    }

    // Prevenir que o erro quebre a aplicação
    event.preventDefault();
  }

  handlePromiseRejection(event) {
    const error = {
      type: 'promise_rejection',
      timestamp: new Date().toISOString(),
      reason: event.reason,
      message: event.reason?.message || String(event.reason),
      stack: event.reason?.stack,
      userAgent: navigator.userAgent,
      url: window.location.href
    };

    this.logError('Promise Rejection', error);
    this.storeError(error);

    // Verificar padrões conhecidos
    if (this.isServiceWorkerError(error)) {
      this.handleServiceWorkerError(error);
    }

    // Prevenir que o erro quebre a aplicação
    event.preventDefault();
  }

  handleConsoleError(...args) {
    const errorMessage = args.join(' ');

    // Detectar erros React específicos
    if (this.isReactError130Message(errorMessage)) {
      const error = {
        type: 'react_error_130',
        timestamp: new Date().toISOString(),
        message: errorMessage,
        args: args,
        userAgent: navigator.userAgent,
        url: window.location.href
      };

      this.handleReactError130(error);
      this.storeError(error);
    }
  }

  isReactError130(error) {
    const message = error.message || error.reason?.message || '';
    return message.includes('Element type is invalid') ||
      message.includes('Minified React error #130');
  }

  isReactError130Message(message) {
    return message.includes('Element type is invalid') ||
      message.includes('Minified React error #130') ||
      message.includes('got: object');
  }

  isServiceWorkerError(error) {
    const message = error.message || error.reason?.message || '';
    return message.includes('A listener indicated an asynchronous response') ||
      message.includes('message channel closed');
  }

  handleReactError130(error) {
    console.group('🚨 REACT ERROR #130 DETECTADO');
    console.error('Erro:', error);
    console.log('🔍 Possíveis causas:');
    console.log('1. Componente importado incorretamente');
    console.log('2. Objeto sendo passado onde React espera função');
    console.log('3. Componente lazy loading com problema');
    console.groupEnd();

    setTimeout(() => {
      console.log('🔄 Recarregando página devido ao erro crítico...');
      window.location.reload();
    }, 3000);
  }

  handleServiceWorkerError(error) {
    console.group('🔧 SERVICE WORKER ERROR');
    console.error('Erro:', error);
    console.log('Este erro é comum e não afeta a funcionalidade');
    console.groupEnd();
  }

  storeError(error) {
    this.errors.unshift(error);

    // Manter apenas os últimos erros
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Salvar no localStorage para debug
    try {
      localStorage.setItem('casal_resolve_errors', JSON.stringify(this.errors.slice(0, 10)));
    } catch (e) {
      console.warn('Não foi possível salvar erros no localStorage:', e);
    }
  }

  logError(type, error) {
    console.group(`🚨 ${type} - ${new Date().toLocaleTimeString()}`);
    console.error('Detalhes:', error);
    console.groupEnd();
  }

  getErrorCount(type) {
    return this.errors.filter(error => error.type === type).length;
  }

  getRecentErrors(minutes = 5) {
    const cutoff = new Date(Date.now() - minutes * 60 * 1000);
    return this.errors.filter(error => new Date(error.timestamp) > cutoff);
  }

  generateErrorReport() {
    const report = {
      total_errors: this.errors.length,
      react_130_errors: this.errors.filter(e => e.type === 'javascript_error' && this.isReactError130(e)).length,
      javascript_errors: this.errors.filter(e => e.type === 'javascript_error').length,
      promise_rejections: this.errors.filter(e => e.type === 'promise_rejection').length,
      user_agent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      recent_errors: this.errors.slice(0, 5)
    };

    console.log('📊 Relatório de Erros:', report);
    return report;
  }

  clearErrors() {
    this.errors = [];
    localStorage.removeItem('casal_resolve_errors');
    console.log('🧹 Erros limpos do monitor');
  }
}

// Criar instância global
const errorMonitor = new ErrorMonitor();

// Expor funções úteis globalmente para debug
window.debugErrorMonitor = {
  getErrors: () => errorMonitor.errors,
  getReport: () => errorMonitor.generateErrorReport(),
  clearErrors: () => errorMonitor.clearErrors(),
  getRecentErrors: (minutes) => errorMonitor.getRecentErrors(minutes)
};

// Comando de debug para console
console.log('🔍 Error Monitor carregado. Use window.debugErrorMonitor para debug');

export default errorMonitor; 